import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import RootLayout from "./components/Layout/RootLayout";
import LoginLayout from "./components/Layout/LoginLayout";
import AdminLayout from "./components/Layout/AdminLayout";
import StudentLayout from "./components/Layout/StudentLayout";
import TeacherLayout from "./components/Layout/TeacherLayout";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLogin from "./pages/LoginPages/AdminLogin";
import Footer from "./components/root/Footer";
import Roles from "./pages/root/Roles";
import KidsLanding from "./pages/Kids/KidsLanding";
import ParentSignup from "./pages/Kids/ParentSignup";
import ParentLogin from "./pages/Kids/ParentLogin";
import ForgotPassword from "./pages/Kids/ForgotPassword";
import KidLogin from "./pages/Kids/KidLogin";
import AddChild from "./pages/Kids/AddChild";
import KidsPlans from "./pages/Kids/KidsPlans";
import KidsReports from "./pages/Kids/KidsReports";
import ParentDashboard from "./pages/Kids/ParentDashboard";
import KidsSettings from "./pages/Kids/KidsSettings";
import KidsWatchAdmin from "./pages/Kids/KidsWatchAdmin";
import KidsWatch from "./pages/Kids/KidsWatch";
import KidsAIChat from "./pages/Kids/KidsAIChat";
import KidsGameHub from "./pages/Kids/Games/KidsGameHub";
import KidsLeaderboard from "./pages/Kids/KidsLeaderboard";
import KidsAchievements from "./pages/Kids/KidsAchievements";
import KidsMultiplayer from "./pages/Kids/KidsMultiplayer";
import KidsRaceResult from "./pages/Kids/KidsRaceResult";
import SchoolAdmin from "./pages/Kids/SchoolAdmin";
import SchoolTeacher from "./pages/Kids/SchoolTeacher";
import KidsIdleLogout from "./components/Kids/KidsIdleLogout";
import KidsShell from "./components/Layout/KidsShell";
import KidsPrivacyPolicy from "./pages/Kids/KidsPrivacyPolicy";
import KidsTermsConditions from "./pages/Kids/KidsTermsConditions";
import KidsFAQPage from "./pages/Kids/KidsFAQPage";
import RefundCancellationPolicy from "./pages/root/RefundCancellationPolicy";
import FAQ from "./pages/root/FAQ";
import KidsDeleteAccountRequest from "./pages/Kids/KidsDeleteAccountRequest";
import QuizGame from "./pages/Kids/Games/QuizGame";
import DragDropGame from "./pages/Kids/Games/DragDropGame";
import TypingGame from "./pages/Kids/Games/TypingGame";
const FlashcardGame = React.lazy(() => import("./pages/Kids/Games/FlashcardGame"));
const TracingGame   = React.lazy(() => import("./pages/Kids/Games/TracingGame"));
const PuzzleGame    = React.lazy(() => import("./pages/Kids/Games/PuzzleGame"));
const MemoryGame    = React.lazy(() => import("./pages/Kids/Games/MemoryGame"));
const ConnectGame   = React.lazy(() => import("./pages/Kids/Games/ConnectGame"));
const SmoothieGame  = React.lazy(() => import("./pages/Kids/Games/SmoothieGame"));
const KidsARLazy    = React.lazy(() => import("./pages/Kids/KidsAR"));

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    AOS.init({
      duration: 1500,
      easing: "ease",
      once: true,
    });
    const lang = i18n.language?.startsWith("ar") ? "ar" : "en";
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [i18n.language]);
  return (
    <BrowserRouter>
      <>
        <KidsIdleLogout />
        <Routes>
        
          <Route path="/*" element={<RootLayout />} />
          <Route path="/login/*" element={<LoginLayout />} />
          <Route path="/signup" element={<Roles Login='signup' />} />
          <Route path="/admin/*" element={<AdminLogin />} />
          <Route path="/admins/*" element={<AdminLayout />} />
          <Route path="/mentees/*" element={<StudentLayout />} />
          <Route path="/mentor/*" element={<TeacherLayout />} />

          {/* ── SCHOOL ADMIN PORTAL ── */}
          <Route path="/school/dashboard" element={<SchoolAdmin />} />
          <Route path="/teacher/dashboard" element={<SchoolTeacher />} />

          {/* ── KIDS SECTION ── */}
          <Route path="/kids" element={<KidsLanding />} />
          <Route path="/kids/signup" element={<ParentSignup />} />
          <Route path="/kids/login" element={<ParentLogin />} />
          <Route path="/kids/forgot-password" element={<ForgotPassword />} />
          <Route path="/kids/kid-login" element={<KidLogin />} />
          <Route path="/kids/parent/dashboard" element={<ParentDashboard />} />
          <Route path="/kids/parent/settings" element={<KidsSettings />} />
          <Route path="/kids/parent/watch" element={<KidsWatchAdmin />} />
          <Route path="/kids/parent/add-child" element={<AddChild />} />
          <Route path="/kids/plans" element={<KidsPlans />} />
          <Route path="/kids/privacy" element={<KidsPrivacyPolicy />} />
          <Route path="/kids/terms" element={<KidsTermsConditions />} />
          <Route path="/kids/faq" element={<KidsFAQPage />} />
          <Route path="/delete-account-request" element={<KidsDeleteAccountRequest />} />
          {/* Store-submission-friendly top-level aliases (Play/App Store expect
              short canonical URLs like cabil.co/privacy-policy) — same content
              as the /kids/* pages, just reachable at the conventional path too. */}
          <Route path="/privacy-policy" element={<KidsPrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<KidsTermsConditions />} />
          <Route path="/refund-policy" element={<RefundCancellationPolicy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/kids/reports" element={<KidsReports />} />
          <Route path="/kids/leaderboard" element={<KidsLeaderboard />} />
          <Route path="/kids/multiplayer" element={<KidsMultiplayer />} />
          <Route path="/kids/multiplayer/race-result" element={<KidsRaceResult />} />
          <Route path="/kids/ai-chat" element={<KidsAIChat />} />
          <Route path="/kids/ar" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-black"><div className="text-white text-4xl animate-bounce">🔮</div></div>}>
              <KidsARLazy />
            </React.Suspense>
          } />

          {/* Persistent bottom tab bar — the child's three destinations */}
          <Route element={<KidsShell />}>
            <Route path="/kids/games" element={<KidsGameHub />} />
            <Route path="/kids/watch" element={<KidsWatch />} />
            <Route path="/kids/achievements" element={<KidsAchievements />} />
          </Route>

          {/* ── KIDS GAMES (gameplay — no tab bar during play) ── */}
          <Route path="/kids/games/quiz/:slug" element={<QuizGame />} />
          <Route path="/kids/games/drag/:slug" element={<DragDropGame />} />
          <Route path="/kids/games/typing/:slug" element={<TypingGame />} />
          <Route path="/kids/games/flashcard/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-600 to-purple-700"><div className="text-white text-6xl animate-bounce">🚗</div></div>}>
              <FlashcardGame />
            </React.Suspense>
          } />
          <Route path="/kids/games/puzzle/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-indigo-700"><div className="text-white text-6xl animate-bounce">🧩</div></div>}>
              <PuzzleGame />
            </React.Suspense>
          } />
          <Route path="/kids/games/tracing/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600"><div className="text-white text-6xl animate-bounce" style={{fontFamily:"'Noto Nastaliq Urdu',serif"}}>ا</div></div>}>
              <TracingGame />
            </React.Suspense>
          } />
          <Route path="/kids/games/memory/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600"><div className="text-white text-6xl animate-bounce">🧠</div></div>}>
              <MemoryGame />
            </React.Suspense>
          } />
          <Route path="/kids/games/connect/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-500 to-emerald-700"><div className="text-white text-6xl animate-bounce">🔗</div></div>}>
              <ConnectGame />
            </React.Suspense>
          } />
          <Route path="/kids/games/smoothie/:slug" element={
            <React.Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-400 to-emerald-600"><div className="text-white text-6xl animate-bounce">🥤</div></div>}>
              <SmoothieGame />
            </React.Suspense>
          } />
        </Routes>
          
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </>
    </BrowserRouter>
  );
}

export default App;
